import React from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  DollarSign,
  Award,
  FileText,
  AlertTriangle,
  Calendar,
  UserCheck,
  CalendarCheck,
} from 'lucide-react';
import api from '../../utils/axios';
import { useAuth } from '../../contexts/AuthContext';
import SummaryCard from '../shared/summary-card';
import StatCardShell from '../shared/stat-card';
import { formatDateOnly } from '../../utils/dateUtils';
import useCardData from './useCardData';
import { CardError } from './DashboardCard';

const DashboardStatCard = ({
  title,
  value,
  icon: Icon,
  color,
  subtitle,
  link,
  loading,
  error,
  onRetry,
  ...rest
}) => (
  <StatCardShell borderColor={color} className="shadow-soft p-6" {...rest}>
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-600">{title}</p>
        {loading && (
          <div role="status" aria-label={`Loading ${title}`} className="mt-1 space-y-2 animate-pulse">
            <div className="h-7 w-24 bg-gray-100 rounded" />
            <div className="h-3 w-16 bg-gray-100 rounded" />
          </div>
        )}
        {!loading && error && (
          <p role="alert" className="mt-1 text-sm text-danger-700">
            Could not load.{' '}
            <button type="button" onClick={onRetry} className="font-medium underline hover:text-danger-800">
              Try again
            </button>
          </p>
        )}
        {!loading && !error && (
          <>
            <p className="text-2xl font-bold text-gray-900 break-words">{value}</p>
            {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
          </>
        )}
      </div>
      <div
        className={`flex-shrink-0 p-3 rounded-full ${color.replace('border-', 'bg-').replace('-500', '-100')}`}
      >
        <Icon className={`h-6 w-6 ${color.replace('border-', 'text-')}`} />
      </div>
    </div>
    {link && (
      <Link
        to={link}
        className="mt-4 inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-500"
      >
        View details
        <svg
          className="ml-1 h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </Link>
    )}
  </StatCardShell>
);

const pkr = (amount) => `₨${Number.parseFloat(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Loads only when `enabled`, so a card the user may not see never makes its request. A 403
// is an answer, not a failure: the grant looked sufficient but the server's scope check said
// no (a department_manager whose account has no department gets one from the headcount), so
// the card is not offered, rather than stuck on an error whose retry can never succeed.
const FORBIDDEN = { forbidden: true };
const useOptionalData = (enabled, load) => useCardData(async () => {
  if (!enabled) return null;
  try {
    return await load();
  } catch (error) {
    if (error.response?.status === 403) return FORBIDDEN;
    throw error;
  }
}, [enabled]);
const available = (gate, result) => gate && !result.data?.forbidden;

const ListPanel = ({ title, link, children }) => (
  <div className="bg-white rounded-lg shadow-soft p-6 min-w-0">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      <Link to={link} className="text-sm text-primary-600 hover:text-primary-500">
        View all
      </Link>
    </div>
    {children}
  </div>
);

/**
 * Who may see each org-level widget. These are company or department figures, so
 * an own-scope grant (every employee's read_own) never opens them: an employee's
 * dashboard showed "Total Employees: 1" and their own pay as "Total Salaries".
 * Documents count the same way: every employee now holds documents:read_own for their
 * own folder (ALEE-2), so the company documents figure and its shortcut need read_all.
 */
export function orgGates({ hasPermission, hasAnyResourceAction }) {
  return {
    employees: hasAnyResourceAction('employees', ['read_all', 'read_department']),
    salaries: hasAnyResourceAction('salaries', ['read_all', 'read_department']),
    appraisals: hasAnyResourceAction('appraisals', ['read_all', 'read_department', 'read_reports']),
    documents: hasPermission('documents', 'read_all'),
    actions: {
      addEmployee: hasPermission('employees', 'create'),
      manageSalaries: hasPermission('salaries', 'read_all'),
      scheduleAppraisal: hasPermission('appraisals', 'read_all'),
      addDocument: hasPermission('documents', 'read_all'),
      attendance: hasAnyResourceAction('attendance', ['read_all', 'read_department']),
    },
  };
}

export const hasOrgOverview = (gates) => gates.employees || gates.salaries || gates.appraisals
  || gates.documents || Object.values(gates.actions).some(Boolean);

/**
 * The organisation-level part of the dashboard (headcount, payroll, appraisals,
 * documents, quick actions, recent hires, upcoming appraisals and alerts), for
 * HR, admins and managers. Each card loads on its own.
 */
const OrgOverview = () => {
  const auth = useAuth();
  const gates = orgGates(auth);

  const employees = useOptionalData(gates.employees, async () => {
    const [statistics, recent] = await Promise.all([
      api.get('/api/employees/statistics'),
      api.get('/api/employees?limit=5'),
    ]);
    return { ...statistics.data.data, recent: recent.data.data.slice(0, 5) };
  });
  const salaries = useOptionalData(gates.salaries, async () => (
    await api.get('/api/salaries/statistics/overview', { params: { is_active: true } })
  ).data.data);
  const appraisals = useOptionalData(gates.appraisals, async () => {
    const [statistics, upcoming] = await Promise.all([
      api.get('/api/appraisals/statistics/overview'),
      api.get('/api/appraisals/upcoming/list?days=30'),
    ]);
    return { ...statistics.data.data, upcoming: upcoming.data.data.slice(0, 5) };
  });
  const documents = useOptionalData(gates.documents, async () => (
    await api.get('/api/documents/statistics/overview')
  ).data.data);

  const show = {
    employees: available(gates.employees, employees),
    salaries: available(gates.salaries, salaries),
    appraisals: available(gates.appraisals, appraisals),
    documents: available(gates.documents, documents),
  };
  const overdue = appraisals.data?.overdue_appraisals?.count || 0;
  const expired = documents.data?.expired_documents?.count || 0;
  const anyAction = Object.values(gates.actions).some(Boolean);
  // Every card the grants offered was refused by the server, and there is nothing to do.
  if (!anyAction && !Object.values(show).some(Boolean)) return null;

  return (
    <section data-testid="dashboard-org" aria-labelledby="dashboard-org-title" className="space-y-6">
      <h2 id="dashboard-org-title" className="text-lg font-semibold text-gray-900">
        Organization overview
      </h2>

      {(show.employees || show.salaries || show.appraisals || show.documents) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-4 gap-6">
          {show.employees && (
            <DashboardStatCard
              data-testid="dashboard-card-employees"
              title="Total Employees"
              value={employees.data?.total ?? 0}
              icon={Users}
              color="border-primary-500"
              subtitle={`${employees.data?.active ?? 0} active`}
              link="/employees"
              loading={employees.loading}
              error={employees.error}
              onRetry={employees.reload}
            />
          )}
          {show.salaries && (
            <DashboardStatCard
              data-testid="dashboard-card-salaries"
              title="Total Salaries (PKR)"
              value={pkr(salaries.data?.total_pkr_salaries?.total)}
              icon={DollarSign}
              color="border-success-500"
              subtitle={`Avg: ${pkr(salaries.data?.avg_pkr_salary?.average)}`}
              link="/salaries"
              loading={salaries.loading}
              error={salaries.error}
              onRetry={salaries.reload}
            />
          )}
          {show.appraisals && (
            <DashboardStatCard
              data-testid="dashboard-card-appraisals"
              title="Pending Appraisals"
              value={appraisals.data?.pending_appraisals?.count || 0}
              icon={Award}
              color="border-warning-500"
              subtitle={`${overdue} overdue`}
              link="/appraisals"
              loading={appraisals.loading}
              error={appraisals.error}
              onRetry={appraisals.reload}
            />
          )}
          {show.documents && (
            <DashboardStatCard
              data-testid="dashboard-card-documents"
              title="Active Documents"
              value={documents.data?.total_documents?.count || 0}
              icon={FileText}
              color="border-info-500"
              subtitle={`${documents.data?.expiring_soon?.count || 0} expiring soon`}
              // Documents live in each employee's profile (ALEE-2), not on a page of their own.
              link="/employees"
              loading={documents.loading}
              error={documents.error}
              onRetry={documents.reload}
            />
          )}
        </div>
      )}

      {anyAction && (
        <div>
          <h3 className="text-base font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {gates.actions.addEmployee && (
              <SummaryCard
                variant="action"
                data-testid="dashboard-action-add-employee"
                cardTitle="Add Employee"
                cardDescription="Register a new employee"
                cardIcon={UserCheck}
                link="/employees/add"
                color="bg-primary-500"
              />
            )}
            {gates.actions.manageSalaries && (
              <SummaryCard
                variant="action"
                data-testid="dashboard-action-manage-salaries"
                cardTitle="Manage Salaries"
                cardDescription="Update salary information"
                cardIcon={DollarSign}
                link="/salaries"
                color="bg-success-500"
              />
            )}
            {gates.actions.scheduleAppraisal && (
              <SummaryCard
                variant="action"
                data-testid="dashboard-action-schedule-appraisal"
                cardTitle="Schedule Appraisal"
                cardDescription="Set up performance reviews"
                cardIcon={Calendar}
                link="/appraisals"
                color="bg-warning-500"
              />
            )}
            {gates.actions.addDocument && (
              <SummaryCard
                variant="action"
                data-testid="dashboard-action-add-document"
                cardTitle="Add Document"
                cardDescription="Open an employee's profile to upload"
                cardIcon={FileText}
                link="/employees"
                color="bg-primary-500"
              />
            )}
            {gates.actions.attendance && (
              <SummaryCard
                variant="action"
                data-testid="dashboard-action-attendance"
                cardTitle="Attendance"
                cardDescription="Daily attendance from #availability"
                cardIcon={CalendarCheck}
                link="/attendance"
                color="bg-warning-500"
              />
            )}
          </div>
        </div>
      )}

      {(show.employees || show.appraisals) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          {show.employees && (
            <ListPanel title="Recent Employees" link="/employees">
              {employees.error && (
                <CardError message="Could not load recent employees." onRetry={employees.reload} />
              )}
              {employees.data && employees.data.recent.length === 0 && (
                <p className="text-sm text-gray-500">No employees yet.</p>
              )}
              <div className="space-y-3">
                {(employees.data?.recent || []).map((employee) => (
                  <div
                    key={employee.id}
                    className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {employee.first_name} {employee.last_name}
                      </p>
                      <p className="text-sm text-gray-500 truncate">{employee.position}</p>
                    </div>
                    <span
                      className={`flex-shrink-0 px-2 py-1 text-xs font-medium rounded-full ${
                        employee.employment_status === 'active'
                          ? 'bg-success-100 text-success-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {employee.employment_status}
                    </span>
                  </div>
                ))}
              </div>
            </ListPanel>
          )}

          {show.appraisals && (
            <ListPanel title="Upcoming Appraisals" link="/appraisals">
              {appraisals.error && (
                <CardError message="Could not load upcoming appraisals." onRetry={appraisals.reload} />
              )}
              {appraisals.data && appraisals.data.upcoming.length === 0 && (
                <p className="text-sm text-gray-500">No appraisals due in the next 30 days.</p>
              )}
              <div className="space-y-3">
                {(appraisals.data?.upcoming || []).map((appraisal) => (
                  <div
                    key={appraisal.id}
                    className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {appraisal.first_name} {appraisal.last_name}
                      </p>
                      <p className="text-sm text-gray-500">
                        Due:{' '}
                        {formatDateOnly(appraisal.next_appraisal_date)}
                      </p>
                    </div>
                    <span
                      className={`flex-shrink-0 px-2 py-1 text-xs font-medium rounded-full ${
                        appraisal.status === 'pending'
                          ? 'bg-warning-100 text-warning-800'
                          : 'bg-success-100 text-success-800'
                      }`}
                    >
                      {appraisal.status}
                    </span>
                  </div>
                ))}
              </div>
            </ListPanel>
          )}
        </div>
      )}

      {((show.appraisals && overdue > 0) || (show.documents && expired > 0)) && (
        <div className="bg-white rounded-lg shadow-soft p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Alerts</h3>
          <div className="space-y-3">
            {show.appraisals && overdue > 0 && (
              <div className="flex items-center p-3 bg-warning-50 border border-warning-200 rounded-lg">
                <AlertTriangle className="h-5 w-5 flex-shrink-0 text-warning-600 mr-3" />
                <div>
                  <p className="font-medium text-warning-800">
                    {overdue} overdue appraisal{overdue > 1 ? 's' : ''}
                  </p>
                  <p className="text-sm text-warning-600">
                    Review and update employee appraisals
                  </p>
                </div>
              </div>
            )}
            {show.documents && expired > 0 && (
              <div className="flex items-center p-3 bg-danger-50 border border-danger-200 rounded-lg">
                <AlertTriangle className="h-5 w-5 flex-shrink-0 text-danger-600 mr-3" />
                <div>
                  <p className="font-medium text-danger-800">
                    {expired} expired document{expired > 1 ? 's' : ''}
                  </p>
                  <p className="text-sm text-danger-600">
                    Update or remove expired documents
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default OrgOverview;
